import { ICustomer } from "shared-types/index";

import { capitalize } from 'lodash-es';
import NetworkConnectionService from 'external-services/network-connection-service/index';
import GeneralSettingsService from "external-services/general-settings-service/generalSettings.service";
import {AxiosInstance, AxiosPromise} from "axios";
import AdminService from "external-services/diary-admin-service/admin.service";

import CustomerStateActions, { CustomerStateActionNames } from "app/actions/customerState.actions";
import VenueActions, { VenueActionNames } from 'app/actions/venue.actions';
import PouchActions, { PouchDocType } from 'app/actions/pouch.actions';
import LoginActions from "app/actions/login.actions";

import CustomerOfflineService from "external-services/customer-offline-service";
import NetworkRequestsService from 'external-services/network-requests-service/index';
import DateUtilsService from "external-services/date-utils-service";

interface ICustomerSearchInput {
  q: string;
  sort?: string;
  start?: number;
  count?: number;
}

export interface ICustomerDifference {
  key: string;
  label?:string;
  original: string|number|boolean;
  new: string|number|boolean;
}
const NS = 'CustomerService'; // jshint ignore:line

const api: AxiosInstance = NetworkRequestsService.getGlobalAPI();

export const getApiInstanceForTests = (): AxiosInstance => api;

class CustomerService {

  public setup(): void {
    LoginActions.handleLogout(() => CustomerOfflineService.deleteCache());
    VenueActions.handleVenueAction(VenueActionNames.VENUE_CHANGE, () => this.setVenue());
    PouchActions.handlePouchSyncedDoctype(PouchDocType.receiveChange, (evt: any, docs: {type: string}[]) => this.remoteChanges(evt, docs));
  }

  public init(): Promise<void> {
    return new Promise((resolve) => {
      this.setVenue().then(() => {
        if (!this.isAPIChannel()) {
          console.log('Loading customers');
          CustomerOfflineService.getAllOffline().then(() => {
            resolve();
          }).catch((err: any) => {
            console.error('Couldn\'t load offline customers on init', err);
            resolve();
          });
        } else {
          resolve();
        }
      });
    });
  }

  public checkDifference(originalCustomer: ICustomer, newCustomer: ICustomer): ICustomerDifference[] {
    const includeCondition = ['birthdayDate', 'birthdayMonth', 'birthdayYear', 'company',
      'email', 'firstName', 'lastName', 'phone', 'postcode', 'streetAddress', 'suburb'];
    const diffInfoList: ICustomerDifference[] = [];
    Object.keys(originalCustomer).forEach((key) => {
      if (originalCustomer[key as keyof ICustomer] !== newCustomer[key as keyof ICustomer] && (includeCondition.includes(key))) {
        if (key === 'phone') {
          if (originalCustomer[key]?.replace(/ /g, '') !== newCustomer[key]?.replace(/ /g, '')) {
            diffInfoList.push({
              key: capitalize(key),
              original: originalCustomer['phone']?.replace(/ /g, '') ?? '',
              new: newCustomer['phone']?.replace(/ /g, '') ?? ''
            });
          }
        } else {
          diffInfoList.push({
            key: capitalize(key),
            original: originalCustomer[key as keyof ICustomer] as string|number|boolean ?? '',
            new: newCustomer[key as keyof ICustomer] as string|number|boolean ?? '',
          });
        }
      }
    });

    diffInfoList.forEach((diffInfo:ICustomerDifference) => {
      switch(diffInfo.key) {
        case 'Birthdaydate':
          diffInfo.label = 'Birth Date';
          break;
        case 'Birthdaymonth':
          diffInfo.label = 'Birth Month';
          break;
        case 'Birthdayyear':
          diffInfo.label = 'Birth Year';
          break;
        case 'Firstname':
          diffInfo.label = 'First Name';
          break;
        case 'Lastname':
          diffInfo.label = 'Last Name';
          break;
        case 'Streetaddress':
          diffInfo.label = 'Street Address';
          break;
        default:
          diffInfo.label = diffInfo.key;
      }

    })
    return diffInfoList;
  }

  /**
   * functions: getById, getByIds, getVenueVisitHistory move to customer-lookup-service
   * search moved to CustomerSearchService
  */

  /**
     * Return exported venue customer in CVS format
     * @param {Date} bookingStartDate
     * @param {Date} bookingEndDate
     * @param {Boolean} hasBookingsOnly
     * @param {Boolean} hasRecordedBirthdayOnly
     */
  public exportCustomers(bookingStartDate: Date, bookingEndDate: Date, hasBookingsOnly: boolean, hasRecordedBirthdayOnly: boolean): AxiosPromise<{data: string;}> {
    const params: {
      bookingStartDate?: string;
      bookingEndDate?: string;
      hasBookingsOnly?: boolean;
      hasRecordedBirthdayOnly?: boolean;
    } = {};

    if (hasBookingsOnly) {
        params.hasBookingsOnly = hasBookingsOnly;
    }

    if (hasRecordedBirthdayOnly) {
        params.hasRecordedBirthdayOnly = hasRecordedBirthdayOnly;
    }

    if (bookingStartDate) {
        params.bookingStartDate = DateUtilsService.dateToIdStr(bookingStartDate);
    }

    if (bookingEndDate) {
        params.bookingEndDate = DateUtilsService.dateToIdStr(bookingEndDate);
    }

    return api.get(AdminService.getVenueEndpointURL() + '/export',{
      timeout: 300000,
      params: params
    });
  }


  // ############################################ Private methods ##################################

  public setVenue(): Promise<void> {
    return new Promise<void>((resolve) => {
        const loadSettings = () => {
          try {
            GeneralSettingsService.get().then(() => {
              resolve();
            });
          } catch (e) {
            setTimeout(() => {
              loadSettings();
            }, 100);
          }
        };

        CustomerOfflineService.deleteCache();
        loadSettings();
    });
  }

  public remoteChanges(evt: any, docs: {type: string}[]): void {
    if (docs && docs.find((d: {type: string}) => d.type === 'customer') != null) {
      // removing this for now to temporarily solve search speed issues
      // console.log('remoteChanges to customers', evt, docs);
      // deleteCache();
      CustomerStateActions.customerStateAction(CustomerStateActionNames.CUSTOMERS_CHANGE);
    }
  }

  private isOffline(): boolean {
    return !NetworkConnectionService.isOnline();
  }

  public isAPIChannel(): boolean {
    return AdminService.getVenue().isOnlineCustomersEnabled && !this.isOffline();
  }




}

const instance = new CustomerService();
export default instance as CustomerService;
